// NexusSLIK System - AI Manager for Client and Barber Performance Index

interface ClientData {
  id: string;
  name: string;
  visits: number | any[];
  totalSpent: number;
  lastVisit: string;
  rating?: number;
  referrals?: number;
  isModelClient?: boolean;
  membershipType?: 'regular' | 'premium' | 'vip' | 'model';
  noShowCount?: number;
  cancellationCount?: number;
  cpiAdjustment?: number; // Manual CPI adjustment from admin
}

interface BarberData {
  appointments?: number;
  totalClients?: number;
  revenue?: number;
  monthlyRevenue?: number;
  rating?: number;
  avgRating?: number;
  specialties?: string[];
  skills?: string[];
  avgServiceTime?: number;
  returningClients?: number;
}

class NexusSLIKSystem {
  CPI = {
    calculateCPI: (client: ClientData) => {
      const visitScore = Math.min((typeof client.visits === 'number' ? client.visits : client.visits?.length || 0) * 5, 100);
      const spendScore = Math.min((client.totalSpent / 100) * 10, 100);
      const retentionScore = client.lastVisit ?
        Math.max(0, 100 - (Date.now() - new Date(client.lastVisit).getTime()) / (1000 * 60 * 60 * 24 * 30) * 10) : 50;
      const referralScore = Math.min((client.referrals || 0) * 20, 100);

      let totalScore = Math.round((visitScore + spendScore + retentionScore + referralScore) / 4);

      // Model client bonus: +10 points (helps with Emergency Booking eligibility)
      if (client.isModelClient || client.membershipType === 'model') {
        totalScore = Math.min(totalScore + 10, 100);
      }

      // Penalty for no-shows and cancellations
      const reliabilityPenalty = ((client.noShowCount || 0) * 5) + ((client.cancellationCount || 0) * 2);
      totalScore = Math.max(0, totalScore - reliabilityPenalty);

      // Apply manual CPI adjustment (admin override)
      if (client.cpiAdjustment) {
        totalScore = Math.max(0, Math.min(100, totalScore + client.cpiAdjustment));
      }

      const tier = totalScore >= 90 ? { name: 'Diamond', color: 'purple', icon: '💎', discount: 20 } :
                   totalScore >= 75 ? { name: 'Platinum', color: 'gray', icon: '🏆', discount: 15 } :
                   totalScore >= 60 ? { name: 'Gold', color: 'yellow', icon: '🥇', discount: 10 } :
                   totalScore >= 40 ? { name: 'Silver', color: 'gray', icon: '🥈', discount: 5 } :
                   { name: 'Bronze', color: 'orange', icon: '🥉', discount: 0 };

      const benefits = [
        'Priority booking',
        'Exclusive discounts',
        'Birthday rewards',
        'Family member benefits'
      ].slice(0, Math.floor(totalScore / 25) + 1);

      const nextTier = totalScore < 90 ? {
        pointsNeeded: totalScore < 40 ? 40 - totalScore :
                      totalScore < 60 ? 60 - totalScore :
                      totalScore < 75 ? 75 - totalScore :
                      90 - totalScore,
        suggestions: ['Visit more frequently', 'Refer friends', 'Try new services']
      } : null;

      return {
        score: totalScore,
        breakdown: {
          visits: visitScore,
          spending: spendScore,
          retention: retentionScore,
          referrals: referralScore
        },
        tier,
        benefits,
        nextTier
      };
    }
  };

  BPI = {
    calculateBPI: (barber: BarberData) => {
      const productivityScore = Math.min((barber.appointments || 0) * 2, 100);
      const revenueScore = Math.min(((barber.revenue || 0) / 100) * 2, 100);
      const ratingScore = (barber.rating || 4) * 20;
      const skillsScore = Math.min((barber.specialties?.length || 0) * 20, 100);

      const totalScore = Math.round((productivityScore + revenueScore + ratingScore + skillsScore) / 4);

      const level = {
        level: totalScore >= 90 ? 'Elite' :
               totalScore >= 75 ? 'Master' :
               totalScore >= 60 ? 'Senior' :
               totalScore >= 40 ? 'Professional' :
               'Junior',
        color: totalScore >= 90 ? 'purple' :
               totalScore >= 75 ? 'gold' :
               totalScore >= 60 ? 'blue' :
               totalScore >= 40 ? 'green' :
               'gray',
        icon: totalScore >= 90 ? '👑' :
              totalScore >= 75 ? '⭐' :
              totalScore >= 60 ? '🎯' :
              totalScore >= 40 ? '✅' :
              '📈'
      };

      const recommendations = [];
      if (productivityScore < 80) recommendations.push('Increase appointment bookings');
      if (revenueScore < 80) recommendations.push('Upsell premium services');
      if (ratingScore < 80) recommendations.push('Focus on client satisfaction');
      if (skillsScore < 80) recommendations.push('Learn new techniques');

      return {
        score: totalScore,
        breakdown: {
          efficiency: productivityScore,
          quality: ratingScore,
          revenue: revenueScore,
          retention: skillsScore
        },
        level,
        recommendations
      };
    }
  };

  private calculateRecencyScore(lastVisit: string): number {
    const now = new Date();
    const last = new Date(lastVisit);
    const daysSince = Math.floor((now.getTime() - last.getTime()) / (1000 * 60 * 60 * 24));

    if (daysSince < 7) return 100;
    if (daysSince < 14) return 80;
    if (daysSince < 30) return 60;
    if (daysSince < 60) return 40;
    return 20;
  }

  private getCPILevel(score: number): string {
    if (score >= 90) return 'VIP';
    if (score >= 75) return 'Loyal';
    if (score >= 60) return 'Regular';
    if (score >= 40) return 'Occasional';
    return 'New';
  }

  private getBPILevel(score: number): string {
    if (score >= 90) return 'Elite';
    if (score >= 75) return 'Expert';
    if (score >= 60) return 'Professional';
    if (score >= 40) return 'Developing';
    return 'Trainee';
  }
}

const NexusSLIK = new NexusSLIKSystem();
export default NexusSLIK;

// Make it globally available for components
if (typeof window !== 'undefined') {
  (window as any).NexusSLIK = NexusSLIK;
}